import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Cookies from "js-cookie";
import NodataFound from '../../component/NodataFound';
import Loader from '../../component/loaderComponent/Loader';
import { MdEdit } from "react-icons/md";
import './AreaExecutive.scss'
import Paginations from '../../component/Pagination/PgmTextField';
import { Switch } from '@mui/material';
import { fetchAreaExecutedListAction, deleteAreaExecutiveAction, toggleAreaExecutiveStatusAction } from '../../actions/areaExecutiveAction';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { RiDeleteBin6Line } from "react-icons/ri";
import DeletePopup from '../../component/DeletePopup/DeletePopup';
import { HTTP_STATUS } from '../../constants/httpstatus/HttpsStatus';
import { NotificationManager } from 'react-notifications';
import { hasAnyPermission, hasPermission } from '../../utils/permissionUtils';
import { PERMISSIONS } from '../../constants/permissions/Permissions';

const AreaExecutive = ({ roles }) => {

  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const language = Cookies.get("language");
  const [isLoading, setIsLoading] = useState(true);
  const [areaExecutives, setAreaExecutives] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;
  const resetPage = 1
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchInput, setSearchInput] = useState('')
  const dispatch = useDispatch();

  const { loading, areaExecutiveList, error } = useSelector((state) => state.getAreaExecutiveListReducer)

  useEffect(() => {
    handleChangeLanguage(language);
    dispatch(fetchAreaExecutedListAction(searchInput, currentPage, pageSize))
  }, [dispatch, currentPage]);

  useEffect(() => {
    if (areaExecutiveList && areaExecutiveList?.results) {
      const updatedAreaExecutives = areaExecutiveList?.results?.map(areaExecutive => ({
        ...areaExecutive,
        toggle: areaExecutive.is_active,
      }));
      setAreaExecutives(updatedAreaExecutives);
      const totalRecords = areaExecutiveList.count || 0;
      setTotalPages(Math.ceil(totalRecords / pageSize));
    } else {
      setAreaExecutives([]);
      setTotalPages(1);
    }
  }, [areaExecutiveList]);

  const handleSwitchToggle = async (index) => {
    const areaExecutive = areaExecutives[index];
    const newStatus = !areaExecutive.is_active;

    const { status, message } = await dispatch(toggleAreaExecutiveStatusAction(areaExecutive.id, newStatus));

    if (status === HTTP_STATUS.SUCCESS) {
      const updatedAreaExecutives = [...areaExecutives];
      updatedAreaExecutives[index].toggle = newStatus;
      updatedAreaExecutives[index].is_active = newStatus;
      setAreaExecutives(updatedAreaExecutives);
      NotificationManager.success(message);
    } else {
      NotificationManager.error(message || t('userManagement.switchFailedCase'));
    }
  };

  const handleChangeLanguage = (language) => {
    i18n.changeLanguage(language);
  };

  const handleEditAreaExecutive = (areaExecutiveId) => {
    const selectedUser = areaExecutives.find(user => user?.id === areaExecutiveId);
    navigate(`/edit_area_executive/${areaExecutiveId}`, { state: { role: selectedUser, roles } })
  }

  const handleDeleteAreaExecuted = async () => {
    if (!selectedUser) return;

    setIsLoading(true);
    const { status, message } = await dispatch(deleteAreaExecutiveAction(selectedUser.id));

    if (status === HTTP_STATUS.SUCCESS) {
      const updatedAreaExecutives = areaExecutives.filter((user) => user?.id !== selectedUser?.id);
      setAreaExecutives(updatedAreaExecutives);
      NotificationManager.success(message);
    } else {
      NotificationManager.error(t('userManagement.deleteFailMsg'));
    }

    setIsLoading(false);
    setIsPopupOpen(false);
  };

  const openDeletePopup = (user) => {
    setSelectedUser(user);
    setIsPopupOpen(true);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);

    if (value.trim() === '') {
      setCurrentPage(resetPage); // Reset page to 1
      dispatch(fetchAreaExecutedListAction('', resetPage, pageSize));
    }
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
    dispatch(fetchAreaExecutedListAction(searchInput, newPage, pageSize));
  };

  const handleSearchClick = () => {
    if (searchInput.trim() === '') {
      setCurrentPage(resetPage);
      dispatch(fetchAreaExecutedListAction('', resetPage, pageSize));
    } else {
      setCurrentPage(resetPage);
      dispatch(fetchAreaExecutedListAction(searchInput, resetPage, pageSize));
    }
  };

  return (
    <div className='area-executive-container'>
      <div className='search-user'>
        <input
          type='text'
          placeholder='Enter Search'
          value={searchInput}
          onChange={(e) => handleInputChange(e)}
        />
        <button disabled={!searchInput.trim()} onClick={handleSearchClick}>{t('common.searchButton')}</button>
      </div>
      <div className='area-executive-table'>
        {loading ? (
          <Loader />
        ) : areaExecutives.length === 0 ? (
          <NodataFound message={t('userManagement.noData')} />
        ) : (
          <div className="table-responsive-wrapper">
            <table>
              <thead>
                <tr>
                  <th>{t('common.sNo')}</th>
                  <th className='user-name-column'>{t('common.username')}</th>
                  <th>{t('common.emailIdLabel')}</th>
                  <th>{t('common.provinceState')}</th>
                  {hasAnyPermission([
                    PERMISSIONS.CAN_ACTIVATE_AND_DEACTIVATE_AREA_EXECUTIVE,
                    PERMISSIONS.CAN_VIEW_AREA_EXECUTIVE_DETAIL,
                    PERMISSIONS.CAN_DELETE_AREA_EXECUTIVE,
                  ]) && <th>{t('common.action')}</th>}
                </tr>
              </thead>
              <tbody>
                {areaExecutives?.map((user, index) => (
                  <tr key={index}>
                    <td>{(currentPage - 1) * pageSize + index + 1}</td>
                    <td className='user-name-column'>{user?.name}</td>
                    <td>{user?.email}</td>
                    <td className='area-executive-state' title={user?.states?.map(state => state?.name).join(', ') || ''}>{user?.states?.map(state => state?.name).join(', ') || ''}</td>
                    {hasAnyPermission([
                      PERMISSIONS.CAN_ACTIVATE_AND_DEACTIVATE_AREA_EXECUTIVE,
                      PERMISSIONS.CAN_VIEW_AREA_EXECUTIVE_DETAIL,
                      PERMISSIONS.CAN_DELETE_AREA_EXECUTIVE,
                    ]) && (
                        <td className='action-btn'>
                          {hasPermission(PERMISSIONS.CAN_VIEW_AREA_EXECUTIVE_DETAIL) && (
                            <button className='edit-btn'>
                              <MdEdit
                                style={{ verticalAlign: 'middle' }}
                                onClick={() => handleEditAreaExecutive(user.id)}
                              />
                            </button>
                          )}
                          {hasPermission(PERMISSIONS.CAN_DELETE_AREA_EXECUTIVE) && (
                            <button
                              className='delete-btn'
                              onClick={() => openDeletePopup(user)}
                            >
                              <RiDeleteBin6Line style={{ verticalAlign: 'middle' }} />
                            </button>
                          )}
                          {hasPermission(PERMISSIONS.CAN_ACTIVATE_AND_DEACTIVATE_AREA_EXECUTIVE) && (
                            <Switch
                              checked={user.is_active}
                              onChange={() => handleSwitchToggle(index)}
                              inputProps={{ 'aria-label': 'controlled' }}
                            />
                          )}
                        </td>
                      )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="pagination-comp">
          <Paginations
            count={totalPages}
            page={currentPage}
            handlePageChange={handlePageChange}
          />
        </div>
      </div>
      <DeletePopup
        title={t('areaExecutive.deleteAreaExecutive')}
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        onConfirm={handleDeleteAreaExecuted}
        message={t('areaExecutive.deleteAreaExecutiveMessage')}
        t={t}
      />
    </div>
  )
}

export default AreaExecutive